// Pure framing maths for the floor view (no three.js import, unit-tested in Node): fitting a floor
// and its HTML department cards into the free part of the canvas, and laying the cards out around
// their pods without covering the other pods.
//
// World points are reduced to camera-basis coordinates {r, u} (world units along the screen's right
// and up axes). A frame maps them to CSS pixels: x = ox + r·scale, y = oy − u·scale. Card boxes are
// pixel offsets around a projected reference point, so they keep their size whatever the zoom.

/** Full department card (px): width, height, gap between the card and what it points at. */
export const CARD = Object.freeze({ w: 184, h: 149, gap: 12 });
/** Compact pill ("● PRODUCTION · 3"): height (px); its width depends on the name (pillWidth). */
export const PILL_H = 26;
/** Agent name tags rise this far (px) above the agents' heads. */
export const TAG_PAD = 26;

const INF = Number.POSITIVE_INFINITY;

/**
 * Estimated width (px) of a compact pill for a department name (mono caps + dot + count).
 * @param {string} name
 * @returns {number}
 */
export function pillWidth(name) {
  const n = [...String(name ?? '')].length;
  return Math.round(Math.min(210, Math.max(92, 56 + n * 6.6)));
}

/**
 * Largest scale (px per world unit) at which every item fits in `area` (minus `margin` on each
 * side), and the offsets that centre the whole set there. Items are points {r, u} with an optional
 * pixel box around their projection {left, top, right, bottom} (negative left/top = extends
 * left/up). The spans are convex in the scale, so the feasible scales form an interval: a
 * bisection finds its upper end.
 * @param {Array<{r:number, u:number, left?:number, top?:number, right?:number, bottom?:number}>} items
 * @param {{left:number, top:number, right:number, bottom:number}} area
 * @param {{margin?:number, minScale?:number, maxScale?:number}} [opts]
 * @returns {{scale:number, ox:number, oy:number, fits:boolean}}
 */
export function fitItems(items, area, { margin = 0, minScale = 0.5, maxScale = 400 } = {}) {
  const list = (items || []).filter((it) => Number.isFinite(it?.r) && Number.isFinite(it?.u));
  const W = Math.max(1, area.right - area.left - 2 * margin);
  const H = Math.max(1, area.bottom - area.top - 2 * margin);
  const lo = Math.max(1e-6, Math.min(minScale, maxScale));
  const hi = Math.max(lo, maxScale);
  const ext = (s) => {
    let x0 = INF; let x1 = -INF; let y0 = INF; let y1 = -INF;
    for (const it of list) {
      const x = it.r * s;
      const y = -it.u * s;
      x0 = Math.min(x0, x + (it.left || 0));
      x1 = Math.max(x1, x + (it.right || 0));
      y0 = Math.min(y0, y + (it.top || 0));
      y1 = Math.max(y1, y + (it.bottom || 0));
    }
    return { x0, x1, y0, y1 };
  };
  const ok = (s) => {
    const e = ext(s);
    return e.x1 - e.x0 <= W + 1e-6 && e.y1 - e.y0 <= H + 1e-6;
  };
  if (!list.length) return { scale: hi, ox: (area.left + area.right) / 2, oy: (area.top + area.bottom) / 2, fits: true };
  let scale;
  let fits = true;
  if (ok(hi)) scale = hi;
  else if (!ok(lo)) { scale = lo; fits = false; } else {
    let a = lo;
    let b = hi;
    for (let i = 0; i < 48 && b - a > 1e-4 * a; i++) {
      const m = (a + b) / 2;
      if (ok(m)) a = m; else b = m;
    }
    scale = a;
  }
  const e = ext(scale);
  const ox = area.left + margin + (W - (e.x1 - e.x0)) / 2 - e.x0;
  const oy = area.top + margin + (H - (e.y1 - e.y0)) / 2 - e.y0;
  return { scale, ox, oy, fits };
}

/**
 * Convex hull of 2D points (Andrew's monotone chain), without repeated end point.
 * @param {Array<{x:number, y:number}>} points
 * @returns {Array<{x:number, y:number}>}
 */
export function convexHull(points) {
  const pts = (points || []).filter((p) => Number.isFinite(p?.x) && Number.isFinite(p?.y))
    .map((p) => ({ x: p.x, y: p.y }))
    .sort((a, b) => a.x - b.x || a.y - b.y);
  if (pts.length < 3) return pts;
  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/** True when two rectangles {left, top, right, bottom} overlap, keeping `gap` apart. */
export function rectsOverlap(a, b, gap = 0) {
  return a.left < b.right + gap && b.left < a.right + gap && a.top < b.bottom + gap && b.top < a.bottom + gap;
}

/**
 * True when a rectangle (grown by `pad`) and a convex polygon intersect (separating axis test).
 * @param {{left:number, top:number, right:number, bottom:number}} rect
 * @param {Array<{x:number, y:number}>} poly convex, any winding
 * @param {number} [pad]
 */
export function rectHitsPolygon(rect, poly, pad = 0) {
  if (!poly?.length) return false;
  const r = { left: rect.left - pad, top: rect.top - pad, right: rect.right + pad, bottom: rect.bottom + pad };
  let minX = INF; let maxX = -INF; let minY = INF; let maxY = -INF;
  for (const p of poly) {
    minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
  }
  if (maxX <= r.left || minX >= r.right || maxY <= r.top || minY >= r.bottom) return false;
  if (poly.length < 3) return true;
  const corners = [[r.left, r.top], [r.right, r.top], [r.right, r.bottom], [r.left, r.bottom]];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const nx = b.y - a.y;
    const ny = a.x - b.x;
    if (!nx && !ny) continue;
    let p0 = INF; let p1 = -INF;
    for (const p of poly) {
      const d = p.x * nx + p.y * ny;
      p0 = Math.min(p0, d); p1 = Math.max(p1, d);
    }
    let q0 = INF; let q1 = -INF;
    for (const [x, y] of corners) {
      const d = x * nx + y * ny;
      q0 = Math.min(q0, d); q1 = Math.max(q1, d);
    }
    if (q1 <= p0 || q0 >= p1) return false;
  }
  return true;
}

function within(rect, bounds) {
  return !bounds || (rect.left >= bounds.left && rect.right <= bounds.right && rect.top >= bounds.top && rect.bottom <= bounds.bottom);
}

function clampNum(v, a, b) {
  return v < a ? a : v > b ? b : v;
}

/**
 * Candidate places of one pod's card, best first: above the pod (centred, then nudged outwards and
 * inwards), below it (the same three), beside it on the outer side, then the inner side.
 */
function candidates(pod, mode, w, h, gap, outward) {
  const out = [];
  const top = (dx, key) => {
    const ax = pod.top.x;
    const ay = pod.top.y;
    const rect = { left: ax + dx - w / 2, right: ax + dx + w / 2, top: ay - gap - h, bottom: ay - gap };
    out.push({ key, mode, side: 'top', ref: 'top', rect, lead: { x1: clampNum(ax, rect.left + 12, rect.right - 12), y1: rect.bottom, x2: ax, y2: ay } });
  };
  top(0, 'top');
  top(outward * w * 0.32, 'top-out');
  top(-outward * w * 0.32, 'top-in');
  const bottom = (dx, key) => {
    const bx = pod.bottom.x;
    const by = pod.bottom.y;
    const rect = { left: bx + dx - w / 2, right: bx + dx + w / 2, top: by + gap, bottom: by + gap + h };
    out.push({ key, mode, side: 'bottom', ref: 'bottom', rect, lead: { x1: clampNum(bx, rect.left + 12, rect.right - 12), y1: rect.top, x2: bx, y2: by } });
  };
  bottom(0, 'bottom');
  bottom(outward * w * 0.32, 'bottom-out');
  bottom(-outward * w * 0.32, 'bottom-in');
  for (const dir of [outward, -outward]) {
    const p = dir > 0 ? pod.right : pod.left;
    const rect = dir > 0
      ? { left: p.x + gap, right: p.x + gap + w, top: p.y - h / 2, bottom: p.y + h / 2 }
      : { left: p.x - gap - w, right: p.x - gap, top: p.y - h / 2, bottom: p.y + h / 2 };
    const side = dir > 0 ? 'right' : 'left';
    out.push({ key: side, mode, side, ref: side, rect, lead: { x1: dir > 0 ? rect.left : rect.right, y1: p.y, x2: p.x, y2: p.y } });
  }
  return out;
}

/**
 * Lays the department cards out around their pods (screen px). Pods are handled from the top of the
 * screen down; each takes the first place that stays inside `bounds`, covers no other pod (their
 * outline includes the desks, the people and their name tags) nor an obstacle, and overlaps no card
 * already placed. Full cards are tried first, then compact pills; a pod left without room gets a
 * pill above it anyway (`forced`). `prev` (the previous result) is tried first, so cards do not
 * jump around while the camera moves.
 * @param {Array<{id:string, name?:string, top:{x:number,y:number}, bottom:{x:number,y:number},
 *   left:{x:number,y:number}, right:{x:number,y:number}, hull:Array<{x:number,y:number}>, pillW?:number}>} pods
 * @param {{card?:{w:number,h:number}, pillH?:number, gap?:number, compact?:boolean, cardsAbove?:boolean,
 *   bounds?:{left:number,top:number,right:number,bottom:number}|null, center?:{x:number,y:number},
 *   obstacles?:Array<Array<{x:number,y:number}>>, prev?:Map<string, {key:string, mode:string}>}} [opts]
 *   compact: pills only; cardsAbove: full cards only above their pod (pills elsewhere)
 * @returns {Map<string, {key:string, mode:'card'|'pill', side:'top'|'bottom'|'left'|'right', ref:string,
 *   rect:{left:number,top:number,right:number,bottom:number}, lead:{x1:number,y1:number,x2:number,y2:number}, forced?:boolean}>}
 */
export function layoutCards(pods, opts = {}) {
  const card = opts.card || CARD;
  const gap = opts.gap ?? CARD.gap;
  const pillH = opts.pillH ?? PILL_H;
  const bounds = opts.bounds || null;
  const list = (pods || []).filter((p) => p && p.top && Number.isFinite(p.top.x) && Number.isFinite(p.top.y));
  const result = new Map();
  if (!list.length) return result;
  const cx = opts.center && Number.isFinite(opts.center.x) ? opts.center.x : list.reduce((s, p) => s + p.top.x, 0) / list.length;
  const order = [...list].sort((a, b) => a.top.y - b.top.y || a.top.x - b.top.x);
  const placed = [];
  const obstacles = opts.obstacles || [];
  for (const pod of order) {
    const outward = pod.top.x >= cx ? 1 : -1;
    const pw = pod.pillW || pillWidth(pod.name);
    const modes = opts.compact ? ['pill'] : ['card', 'pill'];
    const prev = opts.prev?.get?.(pod.id);
    const free = (c) => within(c.rect, bounds)
      && !list.some((o) => o !== pod && rectHitsPolygon(c.rect, o.hull, 3))
      && !obstacles.some((poly) => rectHitsPolygon(c.rect, poly, 3))
      && !placed.some((r) => rectsOverlap(c.rect, r, 6));
    let pick = null;
    for (const mode of modes) {
      let tries = mode === 'card' ? candidates(pod, 'card', card.w, card.h, gap, outward) : candidates(pod, 'pill', pw, pillH, gap - 2, outward);
      if (mode === 'card' && opts.cardsAbove) tries = tries.filter((c) => c.side === 'top');
      // A full card always wins over a pill; within a mode the previous place goes first.
      const i = prev && prev.mode === mode ? tries.findIndex((c) => c.key === prev.key) : -1;
      if (i > 0) tries = [tries[i], ...tries.slice(0, i), ...tries.slice(i + 1)];
      pick = tries.find(free) || null;
      if (pick) break;
    }
    if (!pick) {
      pick = { ...candidates(pod, 'pill', pw, pillH, gap - 2, outward)[0], forced: true };
      if (bounds) {
        // Keep a forced pill on screen.
        const r = pick.rect;
        const dx = r.left < bounds.left ? bounds.left - r.left : r.right > bounds.right ? bounds.right - r.right : 0;
        const dy = r.top < bounds.top ? bounds.top - r.top : 0;
        if (dx || dy) {
          const rect = { left: r.left + dx, right: r.right + dx, top: r.top + dy, bottom: r.bottom + dy };
          const lead = { ...pick.lead, x1: clampNum(pick.lead.x2, rect.left + 12, rect.right - 12), y1: rect.bottom };
          pick = { ...pick, rect, lead };
        }
      }
    }
    placed.push(pick.rect);
    result.set(pod.id, pick);
  }
  return result;
}
